import { getTrackedChangesState, TrackedChangesPluginState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { applyChangeToNodeAttrs, copyNodeWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { getTrackedChangeNodeAttrs, removeTrackedChangesFromNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-changes';
import { canChangeType, clearIncompatible, replaceLinebreaks, replaceNewlines } from '@common/prosemirror/transform/transform.prosemirror';
import { Attrs, Node, NodeType, ResolvedPos } from 'prosemirror-model';
import { Command, EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state';
import { Mapping, ReplaceAroundStep, Step } from 'prosemirror-transform';

export interface SetBlockOptions {
  trackedChangesState: TrackedChangesPluginState,
  // Keep all the add and remove changes grouped together by using the same id
  addTrackedChanges: TrackedChangeNodeAttrs,
  removeTrackedChanges: TrackedChangeNodeAttrs
}

/**
 * Sets the selected textblocks to the given node type with the given attributes.
 * @param tr The transform to add the block changes to.
 * @param from The start of the range to set the block type for.
 * @param to The end of the range to set the block type for.
 * @param isNodeSelection If this is true, don't change the current selection; used for Tracked Changes.
 * @param type The node type to set the selected textblocks to.
 * @param attrs The attributes to set on the new node type.
 * @returns The nodes that had their type changed.
 */
export function setBlock(tr: Transaction, from: number, to: number, isNodeSelection: boolean, type: NodeType, attrs: Attrs | null | ((oldNode: Node) => Attrs), options?: SetBlockOptions): Node[] {
  if (!type.isTextblock) {
    throw new RangeError('Type given to setBlock should be a textblock');
  }

  const changedNodes: Node[] = [];
  const mapFrom: number = tr.steps.length;

  let startM: number;
  tr.doc.nodesBetween(from, to, (node, pos) => {
    let attrsHere: Attrs = typeof attrs === 'function' ? attrs(node) : attrs;
    // If tracked changes are enabled then return the node's attributes with the tracked change applied
    if (options?.trackedChangesState?.enabled) {
      attrsHere = {
        ...node.attrs,
        ...attrsHere,
        ...applyChangeToNodeAttrs(getTrackedChangeNodeAttrs(options?.trackedChangesState.changeMarkType, node), options.addTrackedChanges.changeList[0])
      };
    }
    // Else remove existing tracked changes from the node's existing attributes
    else {
      attrsHere = removeTrackedChangesFromNodeAttrs({
        ...node.attrs,
        ...attrsHere
      });
    }

    if (node.isTextblock && !node.hasMarkup(type, attrsHere) && canChangeType(tr.doc, tr.mapping.slice(mapFrom).map(pos), type) && !attrsHere?.changeList?.some(change => change.changeType === ChangeType.Remove)) {
      let convertNewlines = null;
      if (type.schema.linebreakReplacement) {
        const pre: boolean = type.whitespace === 'pre';
        const supportLinebreak: boolean = !!type.contentMatch.matchType(type.schema.linebreakReplacement);
        if (pre && !supportLinebreak) {
          convertNewlines = false;
        } else if (!pre && supportLinebreak) {
          convertNewlines = true;
        }
      }

      // Ensure all markup that isn't allowed in the new node type is cleared
      if (convertNewlines === false) {
        replaceLinebreaks(tr, node, pos, mapFrom);
      }
      clearIncompatible(tr, tr.mapping.slice(mapFrom).map(pos, 1), type, undefined, convertNewlines === null);

      const mapping: Mapping = tr.mapping.slice(mapFrom);
      startM = mapping.map(pos, 1);
      let endM: number = mapping.map(pos + node.nodeSize, 1);

      // If tracked changes are enabled then apply the add changes to the textblock
      if (options?.trackedChangesState?.enabled) {
        tr.replaceRangeWith(startM, endM, copyNodeWithChanges(options?.trackedChangesState.changeMarkType, node, options.addTrackedChanges, 0, 0, { depth: 0, inFirstBranch: true, inLastBranch: true, isParentTextBlock: true }));
        // Remap the new textblock ending to account for removed children
        endM = tr.mapping.map(endM);
      }

      tr.setNodeMarkup(startM, type, attrsHere, node.marks);

      if (convertNewlines === true) {
        replaceNewlines(tr, node, pos, mapFrom);
      }

      changedNodes.push(node);

      return false;
    }
  });

  // Insert a tracked removal of the changed nodes after the range
  if (options?.trackedChangesState?.enabled) {
    // Get the steps in the current selection range
    const currentSteps = tr.steps.filter(step => step instanceof ReplaceAroundStep).slice(-changedNodes.length);
    let trackedDeleteGroup: Node[] = [];
    for (let i = 0; i < currentSteps.length; i++) {
      const step: Step = currentSteps[i];

      trackedDeleteGroup.push(changedNodes[i]);
      // Group deletes with adjacent wrap steps together
      if (step.to !== currentSteps[i + 1]?.from) {
        // When the next step is not adjacent, insert the group of tracked delete changes
        let insertPos: number = tr.mapping.map(step.to);

        for (const node of trackedDeleteGroup) {
          tr.insert(insertPos, copyNodeWithChanges(options?.trackedChangesState.changeMarkType, node, options.removeTrackedChanges, 0, 0, { depth: 0, inFirstBranch: true, inLastBranch: true, isParentTextBlock: true }));
          // Move the insert position past the node that was inserted
          insertPos += node.nodeSize;
        }

        trackedDeleteGroup = [];
      }
    }

    if (!isNodeSelection) {
      if (typeof startM === 'number') {
        // grabs the first inserted node end position
        const insertNodeEndPos = startM + tr.doc.resolve(startM).nodeAfter.nodeSize - 1;
        // Try to put the selection within the first inserted node , if not then at the end of it
        if (insertNodeEndPos > from) {
          // if the end of the inserted node is before the upper bound use the end position of the first inserted node, otherwise use the selection's upper bound
          const resolvedToPos$ = insertNodeEndPos < to ? tr.doc.resolve(insertNodeEndPos) : tr.doc.resolve(to);
          tr.setSelection(new TextSelection(tr.doc.resolve(from), resolvedToPos$));
        } else {
          // If the end of the inserted node is before the selection's lower bound then force the location to be at the end of the first inserted node
          tr.setSelection(new TextSelection(tr.doc.resolve(insertNodeEndPos)));
        }
      } else {
        // default case where we don't know the first inserted node's start location
        tr.setSelection(new TextSelection(tr.doc.resolve(from)));
      }
    } else if (typeof startM === 'number') {
      // node selection should still remain
      tr.setSelection(new NodeSelection(tr.doc.resolve(startM)));
    }
  }

  return changedNodes;
}

/**
 * Returns a command that tries to set the selected textblocks to the given node type with the given attributes.
 * @param nodeType The node type to set the selected textblocks to.
 * @param attrs The attributes to set on the new node type.
 * @returns The command.
 */
export function setBlockType(nodeType: NodeType, attrs: Attrs | null = null): Command {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    let applicable: boolean = false;

    for (let i = 0; i < state.selection.ranges.length && !applicable; i += 1) {
      const { $from: { pos: from }, $to: { pos: to } } = state.selection.ranges[i];

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (applicable) {
          return false;
        }

        if (!node.isTextblock || node.hasMarkup(nodeType, attrs)) {
          return;
        }

        if (node.type == nodeType) {
          applicable = true;
        } else {
          const $pos: ResolvedPos = state.doc.resolve(pos);
          const index: number = $pos.index();
          applicable = $pos.parent.canReplaceWith(index, index + 1, nodeType);
        }
      });
    }

    if (!applicable) {
      return false;
    }

    if (dispatch) {
      const trackedChangesState: TrackedChangesPluginState = getTrackedChangesState(state);
      // Declare the tracked changes now to keep them all together
      const options: SetBlockOptions = {
        trackedChangesState: trackedChangesState,
        addTrackedChanges: trackedChangesState?.user ? trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add) : null,
        removeTrackedChanges: trackedChangesState?.user ? trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove) : null
      };
      const tr: Transaction = state.tr;

      // Set the selected textblocks to the new node type
      for (let i = 0; i < state.selection.ranges.length; i += 1) {
        const { $from, $to } = state.selection.ranges[i];
        const isNodeSelection = state.selection instanceof NodeSelection;
        setBlock(tr, $from.pos, $to.pos, isNodeSelection, nodeType, attrs, options);
      }

      // Mark that the changes were tracked
      if (trackedChangesState?.enabled) {
        tr.setMeta(TrackedChangesTrackedMetaKey, true);
      }

      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}
